// styles
import styled from 'styled-components';
import SoFormCandidateBlockDetailsInputDay from './SoFormCandidateBlockDetailsInputDay';
import SoFormCandidateBlockDetailsInputLabel from './SoFormCandidateBlockDetailsInputLabel';
import moment from 'moment-timezone';
import SoFlexHourPicker from './SoFlexHourPicker';
import { useSoFormStepsApi } from 'refactored/features/soForm/SoForm.context';
import { useTranslation } from 'react-i18next';

const DEFAULT_TRAVEL_TIME = 60;

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 8px;

  width: 276px;

  /* Inside auto layout */

  flex: none;
  order: 0;
  flex-grow: 1;
`;

const getFormattedDateFromDate = (date) => {
  return moment(date).format('dddd D MMMM');
};

const SoFormCandidateBlockDetailsInputsStandard = ({
  candidate,
  className,
}) => {
  const { t } = useTranslation('Catalog');

  const stepsApi = useSoFormStepsApi();
  const travelTimeInMinutes =
    candidate.formData.travelTimeInMinutes || DEFAULT_TRAVEL_TIME;
  const shipment = candidate.formData.quotePayload.shipments[0];
  const dropoff_from = shipment.dropoff_time_from;

  //   pickup
  const minDateForPickup = new Date(candidate.formData.minTimeForStandard);
  const inputDayPickupData = {
    formattedDate: getFormattedDateFromDate(
      new Date(shipment.pickup_time_from)
    ),
    value: new Date(shipment.pickup_time_from),
    minDate: minDateForPickup,
    handleChange: (e) => {
      const newTimes = {};
      const currentDate = moment(shipment.pickup_time_from)
        .hours(0)
        .minutes(0)
        .seconds(0)
        .milliseconds(0);
      const nextDate = moment(e).hours(0).minutes(0).seconds(0).milliseconds(0);
      const differenceInDays = moment(nextDate).diff(currentDate, 'days');
      //   day has not changed, do nothing
      if (differenceInDays === 0) {
        return;
      }
      //   day has changed, we add the number of days to the current dates
      else {
        newTimes.pickup_time_from = moment(shipment.pickup_time_from)
          .add(differenceInDays, 'day')
          .toJSON();
        newTimes.pickup_time_to = moment(shipment.pickup_time_to)
          .add(differenceInDays, 'day')
          .toJSON();

        const currentDropoffTimeFrom = moment(shipment.dropoff_time_from);
        const newPickupTimeFrom = moment(newTimes.pickup_time_from);
        const isDropoffFromBeforePickupFrom = currentDropoffTimeFrom.isBefore(
          newPickupTimeFrom,
          'minutes'
        );

        if (isDropoffFromBeforePickupFrom) {
          newTimes.dropoff_time_from = moment(shipment.dropoff_time_from)
            .add(differenceInDays, 'day')
            .toJSON();
        }

        const currentDropoffTimeTo = moment(shipment.dropoff_time_to);
        const newPickupTimeTo = moment(newTimes.pickup_time_to);
        const isDropoffToBeforePickupTo = currentDropoffTimeTo.isBefore(
          newPickupTimeTo,
          'minutes'
        );

        if (isDropoffToBeforePickupTo) {
          newTimes.dropoff_time_to = moment(shipment.dropoff_time_to)
            .add(differenceInDays, 'day')
            .toJSON();
        }
      }
      //   return;
      stepsApi.setFlexHours({ newTimes, candidate });
    },
    disableWeekends: false,
    disabledDays: [],
  };

  //   dropoff
  const minDateForDropoff = new Date(shipment.pickup_time_from);
  const inputDayDropoffData = {
    formattedDate: getFormattedDateFromDate(dropoff_from),
    value: new Date(shipment.dropoff_time_from),
    minDate: minDateForDropoff,
    handleChange: (e) => {
      const newTimes = {};
      const currentDate = moment(shipment.dropoff_time_from)
        .hours(0)
        .minutes(0)
        .seconds(0)
        .milliseconds(0);
      const nextDate = moment(e).hours(0).minutes(0).seconds(0).milliseconds(0);
      const differenceInDays = moment(nextDate).diff(currentDate, 'days');
      //   day has not changed, do nothing
      if (differenceInDays === 0) {
        return;
      }
      //   day has changed, we add the number of days to the current dates
      else {
        newTimes.dropoff_time_from = moment(shipment.dropoff_time_from)
          .add(differenceInDays, 'day')
          .toJSON();

        newTimes.dropoff_time_to = moment(shipment.dropoff_time_to)
          .add(differenceInDays, 'day')
          .toJSON();

        const currentDropoffTimeFrom = moment(newTimes.dropoff_time_from);
        const newPickupTimeFrom = moment(shipment.pickup_time_from);
        const isDropoffFromBeforePickupFrom = currentDropoffTimeFrom.isBefore(
          newPickupTimeFrom,
          'minutes'
        );

        if (isDropoffFromBeforePickupFrom) {
          newTimes.dropoff_time_from = moment(shipment.pickup_time_from)
            .add(travelTimeInMinutes, 'minutes')
            .toJSON();
        }

        const currentDropoffTimeTo = moment(newTimes.dropoff_time_to);
        const newPickupTimeTo = moment(shipment.pickup_time_to);
        const isDropoffToBeforePickupTo = currentDropoffTimeTo.isBefore(
          newPickupTimeTo,
          'minutes'
        );

        if (isDropoffToBeforePickupTo) {
          newTimes.dropoff_time_to = moment(shipment.pickup_time_to)
            .add(travelTimeInMinutes, 'minutes')
            .toJSON();
        }
      }
      stepsApi.setFlexHours({ newTimes, candidate });
    },
    disableWeekends: false,
    disabledDays: [],
  };

  return (
    <div className={className}>
      <InputContainer>
        <SoFormCandidateBlockDetailsInputLabel>
          {t('price.standard.pickup')}
        </SoFormCandidateBlockDetailsInputLabel>
        <SoFormCandidateBlockDetailsInputDay
          inputDayData={inputDayPickupData}
        />

        <SoFlexHourPicker candidate={candidate} isPickup={true} />
      </InputContainer>
      <InputContainer>
        <SoFormCandidateBlockDetailsInputLabel>
          {t('price.standard.dropoff')}
        </SoFormCandidateBlockDetailsInputLabel>
        <SoFormCandidateBlockDetailsInputDay
          inputDayData={inputDayDropoffData}
        />

        <SoFlexHourPicker candidate={candidate} isPickup={false} />
      </InputContainer>
    </div>
  );
};

export default styled(SoFormCandidateBlockDetailsInputsStandard)`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 12px 16px 16px;
  gap: 16px;

  width: 600px;

  border-radius: 4px 4px 0px 0px;

  /* Inside auto layout */

  flex: none;
  order: 0;
  align-self: stretch;
  flex-grow: 0;
  /* background: pink; */
`;
